// BEGIN FILE: src/astro/feeds/types.ts
import type { PlanetName } from '../../utils/astroEngine';

export type { PlanetName };
export type HouseNum = 1|2|3|4|5|6|7|8|9|10|11|12;

export interface DashaLevel {
  level: 'MD'|'AD'|'PD'|'SD';
  planet: PlanetName;
  from: string; // ISO
  to: string;   // ISO
  /** Natal houses this planet rules (1..12). */
  rules: HouseNum[];
  /** Natal house this planet occupies (1..12). */
  occupies: HouseNum;
  /** Natal houses aspected (Parāśari, house-based). */
  aspects?: HouseNum[];
  /** Transit stats for this dasha period (MD/AD only) */
  transitStats?: {
    beneficSupport: number;  // 0-100%
    maleficPressure: number; // 0-100%
    avgTransits: number;     // avg # of planets transiting target houses
  };
}

export type HouseScores = { [house in HouseNum]: number };

export interface DashaContext {
  window: { from: string; to: string };
  levels: [DashaLevel, DashaLevel, DashaLevel, DashaLevel]; // MD, AD, PD, SD
  houseScores: HouseScores; // −100..+100 per house
  birthDate?: string; // ISO date for age calculation
  gender?: 'male' | 'female' | 'other'; // Gender for karaka-specific rules
  /** Lagna (ascendant) sign number (1-12: Aries to Pisces) for house lordship calculations */
  lagnaSign?: number;
  /** Navamsha (D9) ascendant sign number (1-12) for marriage timing calculations */
  navamshaLagna?: number;
  /** All natal planet positions (for yoga detection and bhava sandhi) */
  natalPlacements?: Partial<Record<PlanetName, { house: HouseNum; aspects?: HouseNum[]; rules?: HouseNum[]; sign?: number; debilitated?: boolean; exalted?: boolean; offsetDeg?: number }>>;
  /** Combustion status of each planet in natal chart */
  combustionMap?: Partial<Record<PlanetName, boolean>>;
  /** Functional classification per lagna (only for 7 classical planets) */
  functional?: Partial<Record<Exclude<PlanetName, 'Rahu' | 'Ketu'>, 'benefic' | 'malefic' | 'neutral'>>;
  /** Blended planet strength percentages (-100 to +100) */
  blendedPct?: Partial<Record<PlanetName, number>>;
}

export interface TransitPosition {
  planet: PlanetName;
  house: HouseNum;               // current transit house (D1 whole sign)
  sign?: number;                 // current transit sign (0-11) for Sade Sati calculations
  aspects?: HouseNum[];          // houses aspected by this transit (house-based)
}

export interface TransitContext {
  positions: TransitPosition[];
  /** For each active dasha planet, which planets are transiting that planet's natal OCCUPIED house right now. */
  planetsTransitingDashaOccupied: Record<PlanetName, PlanetName[]>;
  /** Saturn sign transit windows for Sade Sati calculations (from ephemeris) */
  saturnSignTransits?: {
    /** Sign number (0-11) Saturn is currently transiting */
    currentSign: number;
    /** When Saturn entered current sign */
    ingressDate: string;
    /** When Saturn will exit current sign */
    egressDate: string;
  };
}

export type LifeArea =
  | 'Self' | 'Mother' | 'Father' | 'Spouse' | 'Children'
  | 'Wealth' | 'Career' | 'Property' | 'Health' | 'Travel'
  | 'Investments' | 'Litigation' | 'Spirituality' | 'AccidentRisk';

export interface FeedItem {
  id: string;                 // stable hash of inputs + rule key
  lifeArea: LifeArea;
  severity: 'positive'|'mixed'|'caution'|'alert';
  score: number;              // −100..+100
  message: string;            // full human text (long form, used in detail views)
  shortMessage?: string;      // concise notification summary: "CAUSE LABEL: effect sentence"
  window: { from: string; to: string };
  evidence: string[];         // short machine reasons (debuggable)
  highProbability?: boolean;  // true if Rahu is in any dasha level (MD/AD/PD/SD)
  ruleKey?: string;           // Key of the rule that generated this feed (for filtering natal vs timing)
  private?: boolean;          // true if feed is only viewable by admins (sensitive predictions)
}

// House → LifeArea map (extendable)
export const HOUSE_TO_LIFE: Partial<Record<HouseNum, LifeArea[]>> = {
  1: ['Self','Health','AccidentRisk','Travel'],
  2: ['Wealth'],
  3: ['Travel'],
  4: ['Mother','Property'],
  5: ['Children','Investments'],
  6: ['Health','Litigation'],
  7: ['Spouse','Travel'],
  8: ['AccidentRisk','Health','Investments','Spirituality'],
  9: ['Father','Spirituality','Travel'],
  10: ['Career','Property'],
  11: ['Wealth','Investments','Career'],
  12: ['Spirituality','Travel','Health']
};

// Planet temperament
export const PLANET_NATURE: Record<PlanetName,'benefic'|'malefic'|'conditional'> = {
  Jupiter:'benefic', Venus:'benefic',
  Mercury:'conditional', Moon:'conditional',
  Sun:'malefic', Mars:'malefic', Saturn:'malefic',
  Rahu:'malefic', Ketu:'malefic'
};

// Utility helpers for house math
export function wrap12(n: number) { return ((n%12)+12)%12; }
export function to1(h0: number) { return (wrap12(h0-1)+1) as HouseNum; }

// END FILE
